import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as sourcesApi from "@/services/sourcesApi";

export const sourcesKeys = {
  all: ["sources"] as const,
  list: (includeHidden: boolean) =>
    [...sourcesKeys.all, "list", includeHidden] as const,
  count: () => [...sourcesKeys.all, "count"] as const,
  mapping: (includeHidden: boolean) =>
    [...sourcesKeys.all, "mapping", includeHidden] as const,
  source: (sourceId: string) =>
    [...sourcesKeys.all, "source", sourceId] as const,
  details: (sourceId: string) =>
    [...sourcesKeys.all, "details", sourceId] as const,
  scrapers: () => [...sourcesKeys.all, "scrapers"] as const,
  logosActive: () => [...sourcesKeys.all, "logos", "active"] as const,
  logo: (sourceId: string) => [...sourcesKeys.all, "logo", sourceId] as const,
  sitemapSummary: () => [...sourcesKeys.all, "sitemap", "summary"] as const,
  sitemapBySource: (sourceId: string) =>
    [...sourcesKeys.all, "sitemap", sourceId] as const,
};

export function useSourcesList(includeHidden = true) {
  return useQuery({
    queryKey: sourcesKeys.list(includeHidden),
    queryFn: () => sourcesApi.getSources(includeHidden),
  });
}

export function useSourcesCount() {
  return useQuery({
    queryKey: sourcesKeys.count(),
    queryFn: sourcesApi.getSourcesCount,
  });
}

export function useSourcesMapping(includeHidden = true) {
  return useQuery({
    queryKey: sourcesKeys.mapping(includeHidden),
    queryFn: () => sourcesApi.getSourcesMapping(includeHidden),
  });
}

export function useSource(sourceId: string) {
  return useQuery({
    queryKey: sourcesKeys.source(sourceId),
    queryFn: () => sourcesApi.getSource(sourceId),
    enabled: !!sourceId,
  });
}

export function useSourceDetails(sourceId: string) {
  return useQuery({
    queryKey: sourcesKeys.details(sourceId),
    queryFn: () => sourcesApi.getSourceDetails(sourceId),
    enabled: !!sourceId,
  });
}

export function useAvailableScrapers() {
  return useQuery({
    queryKey: sourcesKeys.scrapers(),
    queryFn: sourcesApi.getAvailableScrapers,
  });
}

export function useActiveSourceLogos() {
  return useQuery({
    queryKey: sourcesKeys.logosActive(),
    queryFn: sourcesApi.getActiveSourceLogos,
  });
}

export function useSourceLogo(sourceId: string) {
  return useQuery({
    queryKey: sourcesKeys.logo(sourceId),
    queryFn: () => sourcesApi.getSourceLogo(sourceId),
    enabled: !!sourceId,
  });
}

export function useSitemapHealthSummary() {
  return useQuery({
    queryKey: sourcesKeys.sitemapSummary(),
    queryFn: sourcesApi.getSitemapHealthSummary,
  });
}

export function useSourceSitemapHealth(sourceId: string) {
  return useQuery({
    queryKey: sourcesKeys.sitemapBySource(sourceId),
    queryFn: () => sourcesApi.getSourceSitemapHealth(sourceId),
    enabled: !!sourceId,
  });
}

export function useUploadSourceLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      file,
      variant,
    }: {
      sourceId: string;
      file: File;
      variant?: sourcesApi.LogoVariant;
    }) => sourcesApi.uploadSourceLogo(sourceId, file, variant),
    onSuccess: (_, { sourceId }) => {
      queryClient.invalidateQueries({ queryKey: sourcesKeys.logosActive() });
      queryClient.invalidateQueries({ queryKey: sourcesKeys.logo(sourceId) });
      queryClient.invalidateQueries({ queryKey: sourcesKeys.list(true) });
      queryClient.invalidateQueries({ queryKey: sourcesKeys.list(false) });
      queryClient.invalidateQueries({ queryKey: sourcesKeys.all });
    },
  });
}

export function usePatchSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      ...patch
    }: {
      sourceId: string;
    } & sourcesApi.SourceUpdateRequest) =>
      sourcesApi.patchSource(sourceId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourcesKeys.all });
    },
  });
}
